/**
 * urql client factory.
 *
 * - Reads the JWT from localStorage on every request via an Authorization
 *   header. We can't snapshot it at client-creation time because the token
 *   only exists after login.
 * - 401/403 responses bubble up to call-sites as urql errors — components
 *   can decide whether to redirect to /login.
 *
 * If we ever add a refresh-token flow, that lives in an exchange placed
 * between fetchExchange and cacheExchange.
 */
import {
  Client,
  cacheExchange,
  createClient,
  fetchExchange,
  mapExchange,
} from "urql";

import { getToken } from "../auth/token";

function endpoint(): string {
  // In dev, relative /graphql/ → Vite proxies to the API (no CORS in dev).
  // In production, the admin is served from admin.kurdishrojava.org.uk and
  // must call api.* directly — nginx on the admin vhost has no /graphql/ location.
  if (import.meta.env.VITE_GRAPHQL_URL) return import.meta.env.VITE_GRAPHQL_URL;
  return import.meta.env.PROD
    ? "https://api.kurdishrojava.org.uk/graphql/"
    : "/graphql/";
}

export function createUrqlClient(): Client {
  return createClient({
    url: endpoint(),
    exchanges: [
      cacheExchange,
      mapExchange({
        onError(error) {
          // Surface non-network errors in dev; production wires this into a toast.
          if (import.meta.env.DEV) {
            // eslint-disable-next-line no-console
            console.warn("[graphql]", error.message, error.graphQLErrors);
          }
        },
      }),
      fetchExchange,
    ],
    fetchOptions: () => {
      const headers: Record<string, string> = {};
      const token = getToken();
      if (token) {
        headers.Authorization = `JWT ${token}`;
      }
      return { headers };
    },
  });
}
